import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Button from "@/components/base/Button";
import { useSiteData } from "@/context/SiteDataContext";
import { useT } from "@/lib/useSiteText";

export default function BodegaDetalle() {
  const { id } = useParams<{ id: string }>();
  const { data, whatsappLink } = useSiteData();
  const t = useT();
  const bodega = data.bodegas.find((b) => b.id === id);
  const [activeImage, setActiveImage] = useState(0);

  if (!bodega) {
    return <Navigate to="/bodegas" replace />;
  }

  const galeria = [
    { url: bodega.imagen_url, alt: bodega.imagen_alt },
    ...bodega.imagenes.filter((img) => img.url),
  ].filter((img) => img.url);

  const activa = galeria[activeImage] ?? galeria[0];

  return (
    <Layout>
      <section className="bg-primary-900 pt-28 md:pt-36 pb-8">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <nav className="text-xs md:text-sm text-background-300 flex items-center gap-2">
            <Link to="/" className="hover:text-background-50 transition">
              {t("comun.migas_inicio")}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/bodegas" className="hover:text-background-50 transition">
              {t("bodegas.detalle.migas_bodegas")}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-100">{bodega.nombre}</span>
          </nav>
          <h1 className="mt-4 font-heading font-bold text-2xl md:text-4xl text-background-50">{bodega.nombre}</h1>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <div className="lg:col-span-3">
              {activa ? (
                <img src={activa.url} alt={activa.alt || bodega.nombre} className="w-full h-72 md:h-[420px] object-cover rounded-lg" />
              ) : (
                <div className="w-full h-72 md:h-[420px] rounded-lg bg-background-200" />
              )}
              {galeria.length > 1 && (
                <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {galeria.map((img, i) => (
                    <button
                      key={img.url + i}
                      type="button"
                      onClick={() => setActiveImage(i)}
                      className={`rounded-md overflow-hidden border-2 ${i === activeImage ? "border-primary-500" : "border-transparent"}`}
                    >
                      <img src={img.url} alt="" className="w-full h-16 object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="lg:col-span-2">
              <span
                className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${
                  bodega.disponible ? "bg-primary-100 text-primary-800" : "bg-background-200 text-foreground-700"
                }`}
              >
                {bodega.disponible ? t("bodegas.badge_disponible") : t("bodegas.badge_ocupado")}
              </span>
              <p className="mt-4 text-sm text-foreground-500">
                <span className="font-heading font-bold text-2xl text-foreground-900">{bodega.m2} m²</span>
              </p>
              <p className="mt-1 text-foreground-600">
                {t("bodegas.precio_desde")}{" "}
                <strong className="text-foreground-900">UF {bodega.valor_uf.toLocaleString("es-CL")}</strong>{" "}
                {t("bodegas.precio_sufijo")}
              </p>

              <ul className="mt-6 space-y-2.5">
                {bodega.caracteristicas.map((c) => (
                  <li key={c} className="flex items-center gap-2.5 text-sm text-foreground-700">
                    <span className="w-5 h-5 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
                      <i className="ri-check-line text-xs" />
                    </span>
                    {c}
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-col gap-3">
                <Button
                  href={whatsappLink(t("wa.cotizar_bodega", { nombre: bodega.nombre }))}
                  variant="accent"
                  icon="ri-whatsapp-line"
                >
                  {t("bodegas.detalle.whatsapp")}
                </Button>
                <Button to="/contacto" variant="outline">
                  {t("bodegas.detalle.formulario")}
                </Button>
                <Link to="/bodegas" className="text-sm text-center text-foreground-500 hover:text-foreground-700 transition mt-1">
                  {t("bodegas.detalle.volver")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
